import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { type LocationQuery, useRoute, useRouter } from 'vue-router'

import { DEFAULT_CLIDS, NATIVE_APP_CLIDS, PWA_APP_CLIDS, TEST_GAMES, UNKNOWN_CLID } from '@/constants/general'
import { ROUTE_NAMES } from '@/router/constants'
import { isDebugQueryEnabled, isDevtoolsQueryEnabled, SSR_QUERY } from '@/router/query-params'
import type { SelectedExperimentsData } from '@/server/shared/experiments/types'
import type { ClientType, PlatformType, RankingParams } from '@/types'
import { compareVersionStrings } from '@/utils/device'
import type { UACParams } from '@/utils/uac-cookie'

export type ErrorStatus = 404 | 410 | 500
export type PageStatus = 200 | 301 | 302 | ErrorStatus
export type AppBootstrapContext = {
    host: string
    initialQuery: LocationQuery
    platform: PlatformType
    isNativeIos: boolean
    isNativeAndroid: boolean
    appVersion: string | undefined
    countryCode: string | undefined
    clid: string
    experiments: SelectedExperimentsData
    rankingParams: RankingParams
    uacParams: UACParams
}

const DEFAULT_PLATFORM = 'desktop'
const IOS_NATIVE_PAYMENTS_MIN_VERSION = '1.1.0'
const ANDROID_NATIVE_PAYMENTS_MIN_VERSION = '1.0.35'

export const useAppStore = defineStore('app', () => {
    const route = useRoute()

    const bootstrapContext = ref<AppBootstrapContext>()
    const isLoggingEnabled = ref(true)

    const platform = computed(() => bootstrapContext.value?.platform || DEFAULT_PLATFORM)
    const host = computed(() => bootstrapContext.value?.host)
    const countryCode = computed(() => bootstrapContext.value?.countryCode)
    const rawRankingParams = computed(() => bootstrapContext.value?.rankingParams)
    const uacParams = computed(() => bootstrapContext.value?.uacParams || {})
    const initialQuery = computed(() => bootstrapContext.value?.initialQuery || {})
    const initialClid = computed(() => bootstrapContext.value?.clid)
    const isNativeIos = computed(() => bootstrapContext.value?.isNativeIos || false)
    const isNativeAndroid = computed(() => bootstrapContext.value?.isNativeAndroid || false)
    const appVersion = computed(() => bootstrapContext.value?.appVersion)
    const experimentsRaw = computed(() => bootstrapContext.value?.experiments)
    const isGameDistribution = computed(() => !!initialQuery.value[SSR_QUERY.GAME_DISTRIBUTION])
    const isDebugMode = computed(() => isDebugQueryEnabled(initialQuery.value[SSR_QUERY.DEBUG]))
    const withDebugInfo = computed(
        () => isDebugMode.value || isDevtoolsQueryEnabled(initialQuery.value[SSR_QUERY.DEVTOOLS]),
    )
    const isNativeIosEnv = computed(() => isNativeIos.value)
    const isNativeAndroidEnv = computed(() => isNativeAndroid.value)
    const isNativeAppEnv = computed(
        () => isNativeIosEnv.value || isNativeAndroidEnv.value || Object.values(NATIVE_APP_CLIDS).includes(clid.value),
    )
    const isPWA = computed(() => Object.values(PWA_APP_CLIDS).includes(clid.value))
    const isAppEnv = computed(() => isNativeAppEnv.value || isPWA.value)

    const clientType = computed((): ClientType => {
        if (isNativeAppEnv.value) {
            return 'mobile-native'
        }
        if (platform.value !== 'desktop') {
            return 'mobile-web'
        }
        return 'desktop-web'
    })

    function isVersionAtLeast(minVersions: { ios?: string; android?: string }): boolean {
        if (!appVersion.value) {
            return false
        }
        if (isNativeIosEnv.value && minVersions.ios) {
            return compareVersionStrings(appVersion.value, minVersions.ios)
        }
        if (isNativeAndroidEnv.value && minVersions.android) {
            return compareVersionStrings(appVersion.value, minVersions.android)
        }
        return false
    }

    const isNativePaymentsSupported = computed(() =>
        isVersionAtLeast({ ios: IOS_NATIVE_PAYMENTS_MIN_VERSION, android: ANDROID_NATIVE_PAYMENTS_MIN_VERSION }),
    )
    const isPurchaseEnabled = computed(() => (isNativeAppEnv.value ? isNativePaymentsSupported.value : true))
    const isExportOnWidget = computed(() => initialQuery.value[SSR_QUERY.EXPORT_MODE] === 'widget')
    const isExportOnWhitelabel = computed(() => initialQuery.value[SSR_QUERY.EXPORT_MODE] === 'wl')
    const isPartnerEmbed = computed(
        () =>
            (route.name === ROUTE_NAMES.EXPORT_GAME_PAGE && initialQuery.value[SSR_QUERY.EXPORT_MODE] !== 'portal') ||
            route.name === ROUTE_NAMES.WIDGET_GAME_PAGE,
    )
    const isPortalEnv = computed(() => !isPartnerEmbed.value)
    const clid = computed(() => {
        if (TEST_GAMES.includes(route.params.game as string)) {
            return DEFAULT_CLIDS.directTest
        }

        return initialClid.value || UNKNOWN_CLID
    })

    const contentScope = computed(() => {
        // If isAppEnv is true, always return 'curated'
        if (isAppEnv.value) {
            if (isNativeIosEnv.value) {
                // temporary solution for limiting ios app content
                // no in-app, age 16+
                return 'curated-app'
            }
            return 'curated'
        }
        return undefined
    })

    const rankingParams = computed(() => {
        return rawRankingParams.value
    })

    // Here we add conditions for experiments and set dependency between exps
    const experiments = computed(() => {
        const newMainCategory = isNativeAppEnv.value ? undefined : experimentsRaw.value?.['new-main-category']
        const heroVideoAutoplay = newMainCategory ? undefined : experimentsRaw.value?.['hero-video-autoplay-exp']
        const h5RewardedAuction = experimentsRaw.value?.['h5-rewarded-auction-step1']
        return {
            'new-main-category': newMainCategory,
            'hero-video-autoplay-exp': heroVideoAutoplay,
            'h5-rewarded-auction-step1': h5RewardedAuction,
        } as const
    })

    const isH5RewardedAuction = computed(
        () => experiments.value['h5-rewarded-auction-step1']?.sample === 'h5-rewarded-auction-step1',
    )

    // Here we chose which ONE exp group to send to ad targeting (GAM, Prebid, SSP, etc)
    // It can be only one, so one exp can be tracked at time
    const adExpTargeting = computed(() => {
        if (experiments.value['h5-rewarded-auction-step1']) {
            return experiments.value['h5-rewarded-auction-step1'].sample
        } else if (experiments.value['new-main-category']) {
            return experiments.value['new-main-category'].sample
        } else if (experiments.value['hero-video-autoplay-exp']) {
            return experiments.value['hero-video-autoplay-exp'].sample
        }
        return undefined
    })

    function initialize(context: AppBootstrapContext) {
        bootstrapContext.value = context
    }

    function setLoggingEnabled(value: boolean) {
        isLoggingEnabled.value = value
    }

    /* MISC */

    const pageStatus = ref<PageStatus>(200)
    const targetURL = ref<string>('')
    const headers = ref<Record<string, string>>({})

    function setHeaders(value: Record<string, string>) {
        headers.value = value
    }
    function setPageStatus(status: PageStatus) {
        pageStatus.value = status
    }
    function setTargetURL(target: string) {
        targetURL.value = target
    }

    // TODO remove this, and targetURL, setTargetURL, setPageStatus all together
    // use router methods instead
    const router = useRouter()
    watch(
        () => [pageStatus.value, targetURL.value],
        () => {
            if (targetURL.value !== '' && pageStatus.value > 300 && pageStatus.value < 400) {
                router.replace(targetURL.value)
                pageStatus.value = 200
                targetURL.value = ''
            }
        },
    )

    return {
        initialize,
        bootstrapContext,
        headers,
        pageStatus,
        targetURL,
        contentScope,
        clid,
        initialClid,
        host,
        initialQuery,
        platform,
        isGameDistribution,
        isDebugMode,
        withDebugInfo,
        isExportOnWidget,
        isExportOnWhitelabel,
        isPartnerEmbed,
        isPortalEnv,
        rawRankingParams,
        rankingParams,
        uacParams,
        countryCode,
        experimentsRaw,
        experiments,
        adExpTargeting,
        isH5RewardedAuction,
        isPWA,
        isNativeAppEnv,
        isNativeAndroidEnv,
        isNativeIosEnv,
        isNativeIos,
        isNativeAndroid,
        appVersion,
        isAppEnv,
        isPurchaseEnabled,
        isVersionAtLeast,
        clientType,
        isLoggingEnabled,

        /* MISC */
        setPageStatus,
        setHeaders,
        setTargetURL,
        setLoggingEnabled,
    }
})
